/**
 * THE TWO PRESENTATIONAL PIECES THE IDENTITY GRID IS BUILT FROM.
 *
 * Lifted out of `pages/SessionDetail.tsx` for the same reason as
 * `transcript-thread.tsx`: the session viewer renders the same "what this call
 * was" grid, and it has to be the same grid rather than one that looks like it
 * today. See that file's header.
 */
import { useState } from 'react'
import { CheckIcon, CopyIcon } from 'lucide-react'

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="field">
      <span className="label" title={hint}>
        {label}
        {hint && <span aria-hidden> ⓘ</span>}
      </span>
      {children}
    </div>
  )
}

/**
 * A monospace identifier, optionally copyable.
 *
 * COPYABLE MATTERS MORE THAN IT LOOKS. The provider call id is the join key for
 * an OpenAI support ticket, and an `rtc_` id retyped by hand from a screen is an
 * `rtc_` id that does not match anything.
 */
export function Mono({ value, copyable }: { value: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false)
  if (!copyable) return <span className="mono break-all">{value}</span>
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className="mono truncate" title={value}>
        {value}
      </span>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        title={copied ? 'Copied' : 'Copy to clipboard'}
        onClick={() => {
          // No fallback to document.execCommand. This app is served over HTTPS
          // (or localhost), where the async clipboard API is available; a silent
          // fallback path would be dead code that nobody ever tests.
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1400)
          })
        }}
      >
        {copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
        <span className="sr-only">Copy</span>
      </button>
    </span>
  )
}
